import toast from 'react-hot-toast';
import {
  IActivePorts,
  IfFeedLinkStatus,
  SystemInfo,
  WebSocketBroadcastResponse,
  WebSocketStartResponse,
  WebSocketStats,
  WebSocketStopResponse
} from './global';
import { uploadFileToCloud } from './query';

const MAX_ZOOM_LEVEL = 1.2;
const MIN_ZOOM_LEVEL = 0.8;
let currentZoomFactor = 1;

export const browserWindowAPI = {
  // Platform details
  appVersion: async (): Promise<{
    version: string;
    channel: string;
    backendLoaded: boolean;
    collectorRegistered: boolean;
    username: string;
    macAddress: string;
    computerName: string;
  }> => {
    let version = 'WEB';
    let channel = '';
    let username = 'n/a';
    let macAddress = 'n/a';
    let computerName = 'n/a';

    try {
      const packageJson = await fetch('/package.json');
      const packageJsonData = await packageJson.json();

      version = packageJsonData.version.split('-')[0];
      channel = packageJsonData.version.split('-')[1].split('.')[0];
    } catch (error) {
      console.error('Error fetching package.json', error);
    }

    return Promise.resolve({
      version: version,
      channel: channel,
      backendLoaded: true,
      collectorRegistered: false,
      username,
      macAddress,
      computerName
    });
  },

  // Zoom tools
  zoomIn: (): void => {
    if (currentZoomFactor < MAX_ZOOM_LEVEL) {
      currentZoomFactor += 0.1;
      currentZoomFactor = Math.min(currentZoomFactor, MAX_ZOOM_LEVEL);
      document.body.style.transformOrigin = 'top left';
      document.body.style.transform = `scale(${currentZoomFactor})`;
    }
  },
  zoomOut: (): void => {
    if (currentZoomFactor > MIN_ZOOM_LEVEL) {
      currentZoomFactor -= 0.1;
      currentZoomFactor = Math.max(currentZoomFactor, MIN_ZOOM_LEVEL);
      document.body.style.transformOrigin = 'top left';
      document.body.style.transform = `scale(${currentZoomFactor})`;
    }
  },
  zoomReset: (): void => {
    currentZoomFactor = 1;
    document.body.style.transformOrigin = 'top left';
    document.body.style.transform = 'scale(1)';
  },
  getZoom: (): Promise<number> => {
    return Promise.resolve(currentZoomFactor);
  },

  // File operations
  selectFolder: async (_: string): Promise<string> => {
    toast.error('Recurso disponível apenas na versão desktop');
    return '';
  },
  selectFile: async (extensions?: string[]): Promise<string> => {
    const selectedFile = await new Promise<Blob>((resolve, reject) => {
      // Create mocked file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = false;
      input.accept = extensions?.join(',') || '';
      input.style.display = 'none';
      document.body.appendChild(input);

      // Handle file selection
      input.onchange = (event) => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) {
          document.body.removeChild(input);
          resolve(file);
        } else {
          document.body.removeChild(input);
          reject(new Error('Nenhum arquivo selecionado'));
        }
      };

      // Handle cancel
      input.oncancel = () => {
        document.body.removeChild(input);
        reject(new Error('Seleção de arquivo cancelada'));
      };

      // Trigger file selection dialog
      input.click();
    });

    const response = await uploadFileToCloud({
      file: selectedFile
    });

    if (response.status !== 200) {
      throw new Error('Erro ao enviar arquivo para o servidor');
    }

    return response.data.filepath;
  },
  saveFile: async (filename: string, _: string = '.xlsx', buffer?: string | null): Promise<any> => {
    if (!buffer) {
      throw new Error('File buffer is required');
    }

    const binaryString = window.atob(buffer);
    const binaryArray = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      binaryArray[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([binaryArray], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
    a.remove();
  },
  saveFileWithBuffer: async (_: string, __: string, ___: string): Promise<string> => {
    return '';
  },
  downloadOxSpreadsheet: () => {
    const filename = 'Planilha-de-importacao-de-animais-Cargill-Feed-Manager.xlsx';

    try {
      window.open(`/resources/${filename}`, '_blank');
    } catch (error) {
      return { error };
    }

    return { canceled: false };
  },
  downloadTemplateCertifier: () => {
    const filename = 'template-certificadora.xlsx';

    try {
      window.open(`/resources/${filename}`, '_blank');
    } catch (error) {
      return { error };
    }

    return { canceled: false };
  },
  downloadTemplate: async (name: string) => {
    window.open(`/resources/${name}`, '_blank');

    return `/resources/${name}`;
  },
  downloadServerInstaller: () => {
    window.open('/resources/server-installer.exe', '_blank');
  },
  printPreview: async (_: any, __: any) => {
    window.print();
  },
  printDownload: async (_: any) => {
    window.print();
    return { canceled: true };
  },

  // Resources not implemented in the web version
  checkUpdates: async (): Promise<void> => {
    return;
  },
  onUpdatesLog: (_: (event: Event, data: any) => void) => {
    return;
  },
  restartApp: (): void => {
    return;
  },
  quitAndInstall: (): void => {
    return;
  },
  createSheet: async (): Promise<void> => {
    toast.error('Recurso disponível apenas na versão desktop');
    return;
  },
  downloadReport: async (): Promise<number> => {
    toast.error('Recurso disponível apenas na versão desktop');
    return 0;
  },

  // Add serialPort implementation for web version
  serialPort: {
    activePortsList: (): Promise<IActivePorts> => {
      return Promise.resolve({
        RFIDScanner: null,
        weightScale: null,
        barcodeScanner: null
      });
    },
    listSerialPorts: (): Promise<any[]> => {
      return Promise.resolve([]);
    },
    connectSerialPort: (_: any, __: any): Promise<void> => {
      return Promise.resolve();
    },
    disconnectSerialPort: (_: any): Promise<void> => {
      return Promise.resolve();
    },
    pingWeightScale: async (): Promise<{ success: boolean; message: string }> => {
      toast.error('Comunicação serial disponível apenas na versão desktop');
      return Promise.resolve({
        success: false,
        message: 'Comunicação serial disponível apenas na versão desktop'
      });
    },

    invoke: async (_: any): Promise<any> => {
      toast.error('Comunicação serial disponível apenas na versão desktop');
      return [];
    },
    on: (_: any): void => {
      return;
    },
    removeAllListeners: (_: any): void => {
      return;
    },
    send: (_: any): void => {
      return;
    }
  },

  webSocket: {
    getStatus: async (): Promise<WebSocketStats> => {
      return Promise.resolve({
        port: 0,
        isRunning: false,
        connectedClients: 0,
        localIPs: [],
        publicUrl: null
      });
    },
    start: async (): Promise<WebSocketStartResponse> => {
      return Promise.resolve({
        success: true,
        port: 0,
        stats: {
          port: 0,
          isRunning: false,
          connectedClients: 0,
          localIPs: [],
          publicUrl: null
        },
        message: 'WebSocket started'
      } as WebSocketStartResponse);
    },
    stop: async (): Promise<WebSocketStopResponse> => {
      return Promise.resolve({
        success: true,
        message: 'WebSocket stopped'
      } as WebSocketStopResponse);
    },
    broadcast: async (_: any): Promise<WebSocketBroadcastResponse> => {
      return Promise.resolve({
        success: true,
        message: 'WebSocket broadcasted'
      } as WebSocketBroadcastResponse);
    },
    on: (_: string, __: (...args: any[]) => void) => {
      return;
    },
    removeAllListeners: (_: string) => {
      return;
    },
    ping: (): Promise<WebSocketBroadcastResponse> =>
      Promise.resolve({
        success: true,
        message: 'WebSocket pinged'
      }),
    sendSystemInfo: (_: any): Promise<WebSocketBroadcastResponse> =>
      Promise.resolve({
        success: true,
        message: 'WebSocket system info sent'
      }),
    sendCommand: (_: string, __?: any): Promise<WebSocketBroadcastResponse> =>
      Promise.resolve({
        success: true,
        message: 'WebSocket command sent'
      }),
    sendToClient: (_: any, __: any): Promise<WebSocketBroadcastResponse> =>
      Promise.resolve({
        success: true,
        message: 'WebSocket message sent to client'
      }),
    onMessageReceived: (_: any) => {
      return;
    },
    removeMessageReceivedListener: () => {
      return;
    },
    subscribe: (_: string, __: (data: any) => void) => () => {
      return;
    },
    startTunnel: () => Promise.resolve({ success: false, message: 'Sem túnel no web' }),
    stopTunnel: () => Promise.resolve({ success: true })
  },

  getSystemInfo: async (): Promise<SystemInfo> => {
    return Promise.resolve({
      macAddress: '',
      computerName: '',
      username: '',
      ipAddresses: [],
      platform: 'web',
      osVersion: '',
      appVersion: ''
    });
  },

  feedLink: {
    start: (_: string) => {
      return Promise.resolve({ success: true, message: 'Feed link started' });
    },
    stop: (): Promise<void> => Promise.resolve(),
    status: (): Promise<IfFeedLinkStatus> =>
      Promise.resolve({
        running: false,
        directory: null,
        processing: false,
        queueLength: 0,
        pendingCount: 0
      }),
    onSuccess: (_: any) => {
      return;
    },
    onError: (_: any) => {
      return;
    },
    ackSuccess: (_id: string, _ok: boolean, _error?: string) => {
      return;
    },
    removeAllListeners: (_: string) => {
      return;
    }
  }
};
